/* Le scale che portano un dato, non un'impressione (§ 03).
 *
 * **Il colore da solo non basta.** Circa l'otto per cento degli uomini ha una
 * deuteranomalia: per loro rosso, arancio e giallo collassano in un colore
 * solo. Un indicatore che dice la gravita' col tono e basta, per uno su dodici
 * dei nostri operatori non dice niente — quindi tono, glifo e numero viaggiano
 * insieme, sempre e tutti e tre.
 *
 * **Si colora il margine, non la distanza.** «2,98 m» fuori contesto non
 * significa niente: sotto un conduttore sono sei metri di franco da terreno
 * mancati o un margine abbondante da una pianta, secondo che cosa ci sia
 * sotto. `Δ = misurata − minima` mette lo zero sulla soglia, e la scala si
 * legge uguale a 15 kV e a 380 kV.
 *
 * **«Non misurabile» non e' «conforme».** Un buco nella nuvola sotto una
 * chioma fitta e una conformita' accertata sono situazioni opposte: la prima
 * si disegna a tratteggio, non con un colore della scala.
 */

import { notMeasured } from '../format'
import { tx } from '../i18n/t'

export interface Rank {
  /** Il numero che si legge accanto al glifo: `S4`. */
  name: string
  /** Il glifo, per chi il tono non lo distingue. */
  glyph: string
  /** Che cosa comporta, in termini d'esercizio. */
  meaning: string
  /** Il gettone del tono, mai il colore. */
  tone: string
}

/** I cinque ranghi, dal piu' grave. */
export const RANKS: Rank[] = [
  { name: 'S0', glyph: '✓', get meaning() { return tx('rank.s0') }, tone: 'sev-0' },
  { name: 'S1', glyph: '●', get meaning() { return tx('rank.s1') }, tone: 'sev-1' },
  { name: 'S2', glyph: '◆', get meaning() { return tx('rank.s2') }, tone: 'sev-2' },
  { name: 'S3', glyph: '▲', get meaning() { return tx('rank.s3') }, tone: 'sev-3' },
  { name: 'S4', glyph: '▲▲', get meaning() { return tx('rank.s4') }, tone: 'sev-4' },
]

/** Il rango non dichiarato. Non e' lo zero: «nessuno l'ha detto» e «verificato
 * senza violazione» sono cose diverse, e confonderle e' il modo di far sparire
 * un'anomalia in fondo a una coda. */
export const UNRANKED: Rank = {
  get name() {
    return notMeasured()
  },
  glyph: '?',
  get meaning() {
    return tx('rank.unranked')
  },
  tone: 'ink-3',
}

/* Il livello della Specifica Tecnica ha tre gradini, la scala del codice di
 * disegno ne ha cinque. La corrispondenza qui sotto e' **nostra lettura**, non
 * una risposta del committente:
 *
 * | Livello | Rango | Perche' |
 * |---------|-------|---------|
 * | 1       | S4    | va comunicata entro 48 ore dal volo (4.6) |
 * | 2       | S3    | entra nel richiamo R2, intervento programmato |
 * | 3       | S2    | fuori dalla valutazione d'accettazione, ma un lavoro c'e' |
 *
 * Cambiarla si puo' solo insieme alla specifica del committente, e con una
 * regressione sulle consegne d'archivio (§ 13). */
const OF_LEVEL: Record<number, string> = { 1: 'S4', 2: 'S3', 3: 'S2' }

/** Il livello della specifica per un rango, quando ce n'e' uno.
 *
 * S1 non ha corrispondente: la Specifica Tecnica ha tre livelli, la scala del
 * codice di disegno cinque, e il rango piu' lieve resta senza livello da
 * dichiarare. Confermare a S1 significherebbe inventare un livello che il
 * committente non conosce. */
export function levelOfRank(rank: number): number | null {
  const found = Object.entries(OF_LEVEL).find(([, name]) => name === `S${rank}`)
  return found ? Number(found[0]) : null
}

/** Il rango di un livello della specifica. */
export function rankOf(level: number | null): Rank {
  const name = level === null ? undefined : OF_LEVEL[level]
  return RANKS.find((one) => one.name === name) ?? UNRANKED
}

export interface Band {
  /** Che cosa dice il margine, in una parola. */
  meaning: string
  tone: string
  /** Vero dove la misura manca: si disegna a tratteggio, non a colore. */
  unmeasured: boolean
}

export const UNMEASURED: Band = {
  get meaning() {
    return tx('margin.unmeasured')
  },
  tone: 'ink-3',
  unmeasured: true,
}

/** La fascia di un margine, in metri. */
export function bandOf(delta: number | null): Band {
  if (delta === null) return UNMEASURED
  if (delta < 0) return { meaning: tx('margin.violation'), tone: 'sev-4', unmeasured: false }
  if (delta < 0.5) return { meaning: tx('margin.critical'), tone: 'sev-3', unmeasured: false }
  if (delta < 1.5) return { meaning: tx('margin.reduced'), tone: 'sev-2', unmeasured: false }
  return { meaning: tx('margin.compliant'), tone: 'sev-0', unmeasured: false }
}

/** Il margine sulla norma, quando entrambe le grandezze ci sono. */
export function marginOf(measured: number | null, minimum: number | null): number | null {
  if (measured === null || minimum === null) return null
  return measured - minimum
}
